import React, { ReactNode, ReactElement, Children, isValidElement, useMemo } from 'react';
import { Input } from './Input';  // Import Input from the same component library
import { Section } from './Section';  // Import Section from the same component library
import './Container.css';
import { RichText } from '../patterns/RichText';

export interface ContainerProps {
  children: ReactNode;
  variant?: 'blank' | 'default' | 'outline' | 'fill';
  className?: string;
  interactive?: boolean;
  width?: 'fill' | 'hug';
  height?: 'fill' | 'hug';
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  wrapperClasses?: string;
}

const Container: React.FC<ContainerProps> = ({
  interactive,
  children,
  width = 'fill',
  height = 'hug',
  variant = 'default',
  className = '',
  wrapperStyle,
  wrapperClasses,
  style,
}) => {
  // Recursive function to check for Input, RichText, and Section components
  const checkChildren = (children: ReactNode): { hasInput: boolean; hasSection: boolean } => {
    let hasInput = false;
    let hasSection = false;

    Children.forEach(children, (child) => {
      if (isValidElement(child)) {
        if (child.type === Input || child.type === RichText) {
          hasInput = true;
        } else if (child.type === Section) {
          hasSection = true;
        } else if (child.props?.children) {
          // Recursively check the children of this child
          const result = checkChildren(child.props.children);
          hasInput = hasInput || result.hasInput;
          hasSection = hasSection || result.hasSection;
        }
      }
    });

    return { hasInput, hasSection };
  };

  // Memoize the result of checkChildren to prevent unnecessary recalculations
  const { hasInput, hasSection } = useMemo(() => {
    if (interactive === undefined) {
      return checkChildren(children);
    }
    return { hasInput: false, hasSection: false };
  }, [children, interactive]);

  // Determine the dynamicInteractive value
  const dynamicInteractive = interactive !== undefined
    ? interactive
    : hasInput && !hasSection;

  const variantClass = variant ? ` container-${variant}` : '';
  const interactiveClass = dynamicInteractive ? ' interactive middle' : '';

  return (
    <div className={`container-bounds v-stack width-${width} height-${height} ${wrapperClasses ? wrapperClasses : null}`} style={wrapperStyle}>
      <div
        className={`container${interactiveClass}${variantClass} v-stack width-${width} height-${height} ${className} ungroup`}
        style={style}
      >
        {children}
      </div>
    </div>
  );
};

export { Container };
